import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "./ui/button";
import { Menu, X, Lock } from "lucide-react";
import MajorEventsPill from "./MajorEventsPill";
import { MetalWordmark } from "./MetalWordmark";
import { TeaLogo } from "../../components/TeaLogo";
import { TAGLINE_SHORT } from "../brand";

export const DEMO_URL = "https://nexengines.com/demo/exchange-alliance/preview";
export const trackDemo = (source) => navigator.sendBeacon?.(
  `${process.env.REACT_APP_BACKEND_URL}/api/track/demo-click`,
  new Blob([JSON.stringify({ source })], { type: "application/json" }));

// Hash links (#anchor) jump within the Home page. Path links route to sub-pages.
const NAV = [
  { to: "/",                         label: "Home" },
  { to: "/impact",                   label: "Impact" },
  { to: "/employers",                label: "Employers" },
  { to: "/alliances",                label: "Non-Profit Alliances" },
  { to: "/personal-advancement-career-training", label: "Personal Advancement and Career Training" },
  { to: "/supporters",               label: "Supporters" },
  { href: DEMO_URL, label: "Live Demo" },
];

const navId = (label) => `nav-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`;

function NavItem({ item, className, onClick }) {
  if (item.href) {
    return (
      <a href={item.href} target="_blank" rel="noopener noreferrer" data-testid={navId(item.label)}
         onClick={() => { trackDemo("menu"); onClick?.(); }} className={`${className} whitespace-nowrap`}>
        {item.label}
      </a>
    );
  }
  return (
    <Link to={item.to} data-testid={navId(item.label)} onClick={onClick} className={className}>
      {item.label}
    </Link>
  );
}

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${
        scrolled ? "bg-[#1E6F70]/95 backdrop-blur-xl border-b border-[#D4AF37]/50 shadow-[0_1px_0_rgba(212,175,55,0.15)]" : "bg-[#1E6F70]/90 backdrop-blur-md border-b border-[#D4AF37]/35"
      }`}
    >
      <div className="mx-auto max-w-[1440px] px-6 lg:px-8 h-16 flex items-center justify-between gap-4">
        <Link to="/" data-testid="logo-link" className="flex items-center gap-3 group shrink-0">
          {/* Master logo — full medallion, transparent BG, uncropped. Matches
              the master spec: object-contain, no border-radius, no slicing. */}
          <TeaLogo size={48} testid="header-logo-left" className="shrink-0" />
          <div className="leading-tight hidden sm:block">
            <div className="font-display font-bold text-[15px] tracking-tight"><MetalWordmark small allGold testId="header-wordmark" /></div>
            <div className="font-mono text-[9px] tracking-[0.16em] uppercase text-[#F4D88A]/85 whitespace-nowrap" data-testid="header-tagline">{TAGLINE_SHORT}</div>
          </div>
        </Link>

        <nav className="hidden xl:flex items-center gap-5 2xl:gap-7">
          {NAV.map((n) => (
            <NavItem key={n.label} item={n}
              className="font-mono text-[11px] tracking-[0.18em] uppercase text-[#F4D88A] hover:text-white transition-colors whitespace-nowrap" />
          ))}
        </nav>

        <div className="hidden xl:flex items-center gap-3 shrink-0">
          {/* 🩹 PATCH-351 · Gold pill visible only when events exist. */}
          <MajorEventsPill />
          <Button asChild
            className="rounded-none bg-[#D4AF37] hover:bg-[#F4D88A] text-[#0A0612] font-mono text-[11px] tracking-[0.18em] uppercase h-9 px-4">
            <a href="/login" data-testid="header-portal-login">
              <Lock className="h-3.5 w-3.5 mr-2"/> Portal Login
            </a>
          </Button>
          <TeaLogo size={48} testid="header-logo-right" />
        </div>

        <div className="xl:hidden flex items-center gap-3">
          <TeaLogo size={40} testid="header-logo-right-mobile" />
          <button onClick={() => setOpen(!open)} data-testid="mobile-menu-btn"
            className="h-10 w-10 grid place-items-center text-[#F4D88A] border border-[#F4D88A]/50">
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="xl:hidden bg-[#1E6F70] border-t border-[#D4AF37]/40" data-testid="mobile-nav-panel">
          <div className="px-6 py-4 space-y-3">
            {NAV.map((n) => (
              <NavItem key={n.label} item={n} onClick={() => setOpen(false)}
                className="block font-mono text-xs tracking-[0.18em] uppercase text-[#F4D88A] hover:text-white" />
            ))}
            <div className="pt-3 border-t border-[#D4AF37]/40 flex items-center gap-3 flex-wrap">
              {/* 🩹 PATCH-351 · Also surface the events pill on mobile. */}
              <MajorEventsPill compact />
              <a href="/login" className="inline-flex items-center gap-2 font-mono text-xs tracking-[0.18em] uppercase text-[#0A0612] bg-[#D4AF37] hover:bg-[#F4D88A] px-4 py-2">
                <Lock className="h-3.5 w-3.5"/> Portal Login
              </a>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
